import { CountryInput } from '@/components/blocks/inputs/CountryInput';
import { CountrySubdivisionInput } from '@/components/blocks/inputs/CountrySubdivisionInput';
import { Field, FieldContent, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { useFieldContext } from '@/lib/form-context';
import { useStore } from '@tanstack/react-form';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';

interface CountryWithSubdivisionFieldProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  subdivisionFieldName: string;
  subdivisionLabel: string;
  placeholder?: string;
  subdivisionPlaceholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

export const CountryWithSubdivisionField = ({
  label,
  subdivisionFieldName,
  subdivisionLabel,
  placeholder,
  subdivisionPlaceholder,
  readOnly,
  loading,
  disabled,
  variant,
}: CountryWithSubdivisionFieldProps) => {
  const resolvedVariant = variant ?? 'inline';
  const field = useFieldContext<string>();
  const form = field.form;

  const countryErrors = field.state.meta.errors;
  const hasCountryError = field.state.meta.isTouched && countryErrors.length > 0;

  const countryCode = field.state.value;
  const subdivisionValue = useStore(form.store, (state) => state.values[subdivisionFieldName]) as
    string | undefined;

  return (
    <>
      <Field
        orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
        data-invalid={hasCountryError || undefined}
      >
        <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
        <FieldContent>
          {loading ? (
            <Skeleton className="h-4 w-full" />
          ) : readOnly ? (
            <p>{countryCode || '—'}</p>
          ) : (
            <CountryInput
              id={field.name}
              value={countryCode}
              placeholder={placeholder}
              disabled={disabled}
              onBlur={field.handleBlur}
              onChange={(value) => {
                field.handleChange(value);
                form.setFieldValue(subdivisionFieldName, '');
              }}
            />
          )}
        </FieldContent>
      </Field>

      <Field orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}>
        <FieldLabel>{subdivisionLabel}</FieldLabel>
        <FieldContent>
          {loading ? (
            <Skeleton className="h-4 w-full" />
          ) : readOnly ? (
            <p>{subdivisionValue || '—'}</p>
          ) : (
            <CountrySubdivisionInput
              countryCode={countryCode}
              value={subdivisionValue ?? ''}
              placeholder={subdivisionPlaceholder}
              disabled={disabled}
              onChange={(value) => form.setFieldValue(subdivisionFieldName, value)}
            />
          )}
        </FieldContent>
      </Field>
    </>
  );
};
